import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import type { CompanyAccess } from '../../utils/userAccess';

interface User {
    userid: number;
    username: string;
    email: string;
    fullname: string;
    department?: string;
    role: string;
    // Optional per-user modules for access control
    allowedmodules?: string[];
    allowedcompanyids?: number[];
    /** Projects, divisions and departments per company id (utils/userAccess). */
    access?: Record<string, CompanyAccess>;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: !!localStorage.getItem('token'),
    isLoading: false,
    error: null,
};

const clearUiSessionState = () => {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (!key) continue;
            if (
                key === 'selectedCompanyId' ||
                key === 'selectedCompanyName' ||
                key === 'selectedDatabaseName' ||
                key === 'app_windows_v1' ||
                key === 'app_active_window_v1' ||
                key.startsWith('app_windows_') ||
                key.startsWith('app_active_window_')
            ) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {}
};

interface LoginResult {
    user: User | null;
    token: string;
    requirePasswordChange?: boolean;
    passwordChangeData?: { userId: number; username: string };
}

// Async thunks
export const login = createAsyncThunk(
    'auth/login',
    async (
        { username, password }: { username: string; password: string },
        { rejectWithValue }
    ) => {
        try {
            const res = await apiService.login(username, password);
            if (!res?.success) {
                return rejectWithValue(res?.message || 'Login failed');
            }

            if (res?.requirePasswordChange) {
                return {
                    user: null,
                    token: '',
                    requirePasswordChange: true,
                    passwordChangeData: res.data
                } as LoginResult;
            }

            if (!res?.data?.token) {
                return rejectWithValue(res?.message || 'Login failed');
            }
            const { token, user } = res.data;
            clearUiSessionState();
            localStorage.setItem('token', token);
            return { user, token };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Login failed');
        }
    }
);

export const getProfile = createAsyncThunk(
    'auth/getProfile',
    // silent: background refresh (e.g. tab refocus) — must not toggle global
    // loading state and must not end the session on a transient failure
    async (_opts: { silent?: boolean } | undefined, { rejectWithValue }) => {
        try {
            const res = await apiService.getProfile();
            if (!res?.success || !res?.data) {
                return rejectWithValue(res?.message || 'Failed to get profile');
            }
            return res.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to get profile');
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            localStorage.removeItem('token');
            clearUiSessionState();
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Login
        builder.addCase(login.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
            state.isLoading = false;
            if (action.payload.requirePasswordChange) {
                state.user = null;
                state.token = null;
                state.isAuthenticated = false;
            } else {
                state.user = action.payload.user as User;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            }
        });
        builder.addCase(login.rejected, (state, action: PayloadAction<any>) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Get Profile
        builder.addCase(getProfile.pending, (state, action) => {
            if (!action.meta.arg?.silent) {
                state.isLoading = true;
            }
        });
        builder.addCase(getProfile.fulfilled, (state, action: PayloadAction<User>) => {
            state.isLoading = false;
            state.user = action.payload;
        });
        builder.addCase(getProfile.rejected, (state, action) => {
            state.isLoading = false;
            if (action.meta.arg?.silent) {
                // Keep the current session; a background refresh failing
                // (flaky network, server restart) must not log the user out.
                return;
            }
            state.isAuthenticated = false;
            state.token = null;
            localStorage.removeItem('token');
            clearUiSessionState();
        });
    },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
